import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { WishlistHeartComponent } from './wishlist-heart/wishlist-heart.component';
import {
  WishlistService,
  WishlistItem,
  WishlistSummary,
} from '../services/wishlist.service';
import { AuthService } from '../services/auth.service';

interface ExtendedWishlistItem extends WishlistItem {
  images: string[];
  currentImageIndex: number;
  preloadedImages?: Set<number>;
  isImageLoading?: boolean;
  touchStartX?: number;
  touchStartY?: number;
  touchStartTime?: number;
  showCarouselControls?: boolean;
  controlsTimeout?: any;
  hasShownControls?: boolean;
  isTouchScrolling?: boolean;
  touchMoved?: boolean;
  lastTouchX?: number;
  lastTouchY?: number;
}

@Component({
  selector: 'app-wishlist',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    HeaderComponent,
    FooterComponent,
    WishlistHeartComponent,
  ],
  template: `
    <div class="wishlist-page">
      <app-header></app-header>

      <main class="wishlist-content">
        <div class="wishlist-header">
          <div class="header-content">
            <h1 class="page-title">My Wishlist</h1>
            <p class="page-description" *ngIf="!isLoading && summary.total > 0">
              {{ summary.total }} saved
              {{ summary.total === 1 ? 'item' : 'items' }}
            </p>
          </div>
        </div>

        <div class="category-filter" *ngIf="!isLoading && summary.total > 0">
          <div class="filter-content">
            <h3 class="filter-title">Filter by Category</h3>
            <div class="category-buttons">
              <button
                class="category-btn"
                [class.active]="selectedCategory === 'all'"
                (click)="selectCategory('all')"
              >
                All ({{ summary.total }})
              </button>
              <button
                *ngFor="let category of getAvailableCategories()"
                class="category-btn"
                [class.active]="selectedCategory === category.key"
                (click)="selectCategory(category.key)"
              >
                {{ category.label }} ({{ category.count }})
              </button>
            </div>
          </div>
        </div>

        <div class="loading-container" *ngIf="isLoading">
          <div class="loading-spinner"></div>
          <p class="loading-text">Loading your wishlist...</p>
        </div>

        <div class="error-container" *ngIf="error && !isLoading">
          <div class="error-icon">⚠️</div>
          <h3 class="error-title">Failed to Load Wishlist</h3>
          <p class="error-message">{{ error }}</p>
          <button class="retry-button" (click)="loadWishlist()">
            Try Again
          </button>
        </div>

        <div
          class="empty-state"
          *ngIf="!isLoading && !error && wishlistItems.length === 0"
        >
          <h3 class="empty-title">Your wishlist is empty</h3>
          <p class="empty-message">
            Start exploring and save items you love to see them here
          </p>
          <div class="empty-actions">
            <a routerLink="/search" class="browse-btn"> Browse Listings </a>
            <a routerLink="/featured" class="featured-btn"> View Featured </a>
          </div>
        </div>

        <div
          class="wishlist-items"
          *ngIf="!isLoading && !error && filteredItems.length > 0"
        >
          <div class="items-grid">
            <div
              *ngFor="let item of filteredItems; trackBy: trackByItem"
              class="wishlist-item"
              (mouseenter)="onItemHover(item)"
              (mouseleave)="onItemLeave(item)"
            >
              <div
                class="item-image-container"
                (touchstart)="onTouchStart($event, item)"
                (touchmove)="onTouchMove($event, item)"
                (touchend)="onTouchEnd($event, item)"
              >
                <div
                  class="image-wrapper"
                  [class.loading]="item.isImageLoading"
                >
                  <img
                    [src]="item.images[item.currentImageIndex]"
                    [alt]="item.listing.title"
                    class="item-image"
                    loading="lazy"
                    (load)="
                      onImageLoad(item.images[item.currentImageIndex], item)
                    "
                    (error)="onImageError($event, item.listing)"
                  />

                  <div
                    class="image-loading-overlay"
                    *ngIf="item.isImageLoading"
                  >
                    <div class="spinner"></div>
                  </div>
                </div>

                <div
                  class="image-click-zones desktop-only"
                  *ngIf="hasMultipleImages(item)"
                >
                  <div
                    class="click-zone zone-left"
                    (click)="onImageZoneClick($event, item, 'prev')"
                    [attr.aria-label]="'Previous image'"
                  >
                    <div class="zone-icon">
                      <svg viewBox="0 0 24 24" class="zone-svg">
                        <polyline points="15 18 9 12 15 6"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div
                    class="click-zone zone-middle"
                    (click)="onImageZoneClick($event, item, 'view')"
                  ></div>
                  <div
                    class="click-zone zone-right"
                    (click)="onImageZoneClick($event, item, 'next')"
                    [attr.aria-label]="'Next image'"
                  >
                    <div class="zone-icon">
                      <svg viewBox="0 0 24 24" class="zone-svg">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>

                <div
                  class="carousel-controls desktop-only"
                  *ngIf="hasMultipleImages(item) && item.showCarouselControls"
                  [class.fading-out]="!item.showCarouselControls"
                >
                  <div class="carousel-indicator">
                    <svg viewBox="0 0 24 24" class="carousel-icon">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </div>

                  <div class="carousel-indicator">
                    <svg viewBox="0 0 24 24" class="carousel-icon">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                </div>

                <div
                  class="image-counter desktop-only"
                  *ngIf="hasMultipleImages(item)"
                >
                  {{ getImageCounter(item) }}
                </div>

                <div
                  class="image-dots mobile-only"
                  *ngIf="hasMultipleImages(item)"
                >
                  <span
                    *ngFor="let img of item.images; let i = index"
                    class="dot"
                    [class.active]="i === item.currentImageIndex"
                  ></span>
                </div>

                <div class="item-badges">
                  <span *ngIf="item.listing.is_featured" class="badge featured">
                    Featured
                  </span>
                  <span
                    *ngIf="isNewListing(item.listing.created_at)"
                    class="badge new"
                  >
                    New
                  </span>
                </div>
              </div>

              <div class="item-content" (click)="viewListing(item.listing)">
                <div class="item-header">
                  <h3 class="item-title" (click)="viewListing(item.listing)">
                    {{ item.listing.title }}
                  </h3>
                  <app-wishlist-heart
                    [listingId]="item.listing.id"
                    [tableName]="item.listing.table_name"
                    (wishlistChanged)="onWishlistChanged($event, item)"
                  ></app-wishlist-heart>
                </div>

                <div class="item-price">
                  {{ formatPrice(item.listing.price, item.listing.currency) }}
                </div>

                <p class="item-description">
                  {{ truncateDescription(item.listing.description || '') }}
                </p>

                <div class="item-meta">
                  <div class="item-category">
                    <span class="category-badge">
                      {{ getCategoryDisplayName(item.listing.table_name) }}
                    </span>
                  </div>

                  <div class="item-location" *ngIf="item.listing.location">
                    <svg viewBox="0 0 24 24" class="location-icon">
                      <path
                        d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"
                      ></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <span>{{ item.listing.location }}</span>
                  </div>
                </div>

                <div class="item-footer">
                  <div class="item-dates">
                    <span class="wishlisted-date">
                      Saved {{ formatDate(item.wishlisted_at) }}
                    </span>
                    <span class="posted-date">
                      Posted {{ formatDate(item.listing.created_at) }}
                    </span>
                  </div>

                  <button class="view-btn" (click)="viewListing(item.listing)">
                    View Details
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            class="pagination-container"
            *ngIf="pagination && pagination.pages > 1"
          >
            <div class="pagination">
              <button
                class="pagination-btn prev"
                [disabled]="pagination.page <= 1 || isLoadingPage"
                (click)="goToPage(pagination.page - 1)"
              >
                <svg viewBox="0 0 24 24" class="pagination-icon">
                  <path d="M15 18l-6-6 6-6"></path>
                </svg>
                Previous
              </button>

              <div class="pagination-numbers">
                <button
                  *ngFor="let page of getVisiblePages()"
                  class="pagination-number"
                  [class.active]="page === pagination.page"
                  [disabled]="page === '...' || isLoadingPage"
                  (click)="page !== '...' && goToPage(+page)"
                >
                  {{ page }}
                </button>
              </div>

              <button
                class="pagination-btn next"
                [disabled]="
                  pagination.page >= pagination.pages || isLoadingPage
                "
                (click)="goToPage(pagination.page + 1)"
              >
                Next
                <svg viewBox="0 0 24 24" class="pagination-icon">
                  <path d="M9 18l6-6-6-6"></path>
                </svg>
              </button>
            </div>

            <div class="pagination-info">
              Showing {{ getDisplayRange() }} of {{ pagination.total }} items
            </div>
          </div>
        </div>

        <div
          class="modal-overlay"
          *ngIf="showClearConfirmation"
          (click)="showClearConfirmation = false"
        >
          <div class="modal-content" (click)="$event.stopPropagation()">
            <h3 class="modal-title">Clear Wishlist</h3>
            <p class="modal-message">
              Are you sure you want to remove all items from your wishlist? This
              action cannot be undone.
            </p>
            <div class="modal-actions">
              <button
                class="cancel-btn"
                (click)="showClearConfirmation = false"
                [disabled]="isClearing"
              >
                Cancel
              </button>
              <button
                class="confirm-btn"
                (click)="clearWishlist()"
                [disabled]="isClearing"
              >
                {{ isClearing ? 'Clearing...' : 'Clear All' }}
              </button>
            </div>
          </div>
        </div>
      </main>

      <app-footer></app-footer>
    </div>
  `,
  styleUrls: ['./wishlist.component.css'],
})
export class WishlistComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  wishlistItems: ExtendedWishlistItem[] = [];
  filteredItems: ExtendedWishlistItem[] = [];
  summary: WishlistSummary = { total: 0, by_category: {} };
  pagination: any = null;
  loadedImages: Set<string> = new Set();

  private readonly SWIPE_THRESHOLD = 30;
  private readonly SWIPE_FAST_THRESHOLD = 20;
  private readonly SWIPE_VELOCITY_THRESHOLD = 0.3;
  private readonly SWIPE_TIME_THRESHOLD = 600;
  private readonly VERTICAL_SCROLL_THRESHOLD = 25;
  private readonly TAP_MOVEMENT_THRESHOLD = 10;
  private readonly TAP_TIME_THRESHOLD = 300;

  isLoading = true;
  isLoadingPage = false;
  error: string | null = null;
  selectedCategory = 'all';
  currentPage = 1;
  itemsPerPage = 12;
  showClearConfirmation = false;
  isClearing = false;

  constructor(
    private wishlistService: WishlistService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadWishlist();
    this.loadWishlistSummary();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadWishlist(): void {
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: '/wishlist' },
      });
      return;
    }

    this.isLoading = this.currentPage === 1;
    this.error = null;

    const category =
      this.selectedCategory === 'all' ? null : this.selectedCategory;

    this.wishlistService
      .getWishlist(category, this.currentPage, this.itemsPerPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.wishlistItems = response.items.map((item) =>
            this.transformWishlistItem(item)
          );
          this.applyFilter();
          this.pagination = response.pagination;
          this.isLoading = false;
          this.isLoadingPage = false;
        },
        error: (error) => {
          this.error =
            error.message || 'Failed to load wishlist. Please try again.';
          this.isLoading = false;
          this.isLoadingPage = false;
        },
      });
  }

  private transformWishlistItem(item: WishlistItem): ExtendedWishlistItem {
    let images: string[] = [];

    if (item.listing.images && Array.isArray(item.listing.images)) {
      images = item.listing.images;
    } else if (item.listing.first_image) {
      images = [item.listing.first_image];
    } else {
      images = [this.getDefaultImage(item.listing)];
    }

    return {
      ...item,
      images: images,
      currentImageIndex: 0,
      preloadedImages: new Set([0]),
      isImageLoading: false,
      showCarouselControls: false,
      hasShownControls: false,
      isTouchScrolling: false,
      touchMoved: false,
    };
  }

  loadWishlistSummary(): void {
    if (!this.authService.isLoggedIn()) return;

    this.wishlistService
      .getWishlistSummary()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (summary) => {
          this.summary = summary;
        },
        error: (error) => {
          console.error('Failed to load wishlist summary:', error);
        },
      });
  }

  selectCategory(category: string): void {
    this.selectedCategory = category;
    this.currentPage = 1;
    this.loadWishlist();
  }

  applyFilter(): void {
    if (this.selectedCategory === 'all') {
      this.filteredItems = this.wishlistItems;
    } else {
      this.filteredItems = this.wishlistItems.filter((item) => {
        const itemCategory = this.getCategoryFromTableName(
          item.listing.table_name
        );
        return itemCategory === this.selectedCategory;
      });
    }
  }

  getAvailableCategories(): any[] {
    const categories = [
      { key: 'vehicles', label: 'Vehicles' },
      { key: 'real-estate', label: 'Real Estate' },
      { key: 'marketplace', label: 'Marketplace' },
      { key: 'jobs', label: 'Jobs' },
    ];

    return categories
      .filter((cat) => this.summary.by_category[cat.key] > 0)
      .map((cat) => ({
        ...cat,
        count: this.summary.by_category[cat.key] || 0,
      }));
  }

  private isWishlistButtonTouch(event: TouchEvent): boolean {
    const target = event.target as HTMLElement;
    return !!(
      target.closest('.listing-bookmark') ||
      target.closest('app-wishlist-heart')
    );
  }

  onTouchStart(event: TouchEvent, item: ExtendedWishlistItem): void {
    if (this.isWishlistButtonTouch(event)) {
      return;
    }

    const touch = event.touches[0];
    item.touchStartX = touch.clientX;
    item.touchStartY = touch.clientY;
    item.lastTouchX = touch.clientX;
    item.lastTouchY = touch.clientY;
    item.touchStartTime = Date.now();
    item.isTouchScrolling = false;
    item.touchMoved = false;
  }

  onTouchMove(event: TouchEvent, item: ExtendedWishlistItem): void {
    if (this.isWishlistButtonTouch(event)) {
      return;
    }

    if (item.touchStartX === undefined || item.touchStartY === undefined) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - item.touchStartX);
    const deltaY = Math.abs(touch.clientY - item.touchStartY);

    if (
      deltaY > this.TAP_MOVEMENT_THRESHOLD ||
      deltaX > this.TAP_MOVEMENT_THRESHOLD
    ) {
      item.touchMoved = true;
    }

    if (deltaY > deltaX && deltaY > this.VERTICAL_SCROLL_THRESHOLD) {
      item.isTouchScrolling = true;
    } else if (deltaX > deltaY && deltaX > 5 && item.images.length > 1) {
      event.preventDefault();
    }

    item.lastTouchX = touch.clientX;
    item.lastTouchY = touch.clientY;
  }

  onTouchEnd(event: TouchEvent, item: ExtendedWishlistItem): void {
    if (this.isWishlistButtonTouch(event)) {
      this.resetTouchState(item);
      return;
    }

    if (item.images.length <= 1) {
      if (!item.isTouchScrolling && !item.touchMoved) {
        this.viewListing(item.listing);
      }
      this.resetTouchState(item);
      return;
    }

    if (
      item.touchStartX === undefined ||
      item.touchStartY === undefined ||
      item.touchStartTime === undefined
    ) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - item.touchStartX;
    const deltaY = touch.clientY - item.touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const deltaTime = Date.now() - item.touchStartTime;

    if (
      item.isTouchScrolling ||
      (absDeltaY > this.VERTICAL_SCROLL_THRESHOLD && absDeltaY > absDeltaX)
    ) {
      this.resetTouchState(item);
      return;
    }

    const velocity = deltaTime > 0 ? absDeltaX / deltaTime : 0;
    const isHorizontalSwipe = absDeltaX > absDeltaY;
    const isQuickEnough = deltaTime < this.SWIPE_TIME_THRESHOLD;
    const isFastSwipe =
      velocity >= this.SWIPE_VELOCITY_THRESHOLD &&
      absDeltaX >= this.SWIPE_FAST_THRESHOLD;
    const isLongEnough = absDeltaX > this.SWIPE_THRESHOLD || isFastSwipe;
    const isNotTooVertical = absDeltaY < this.VERTICAL_SCROLL_THRESHOLD;

    if (
      isHorizontalSwipe &&
      isQuickEnough &&
      isLongEnough &&
      isNotTooVertical
    ) {
      event.stopPropagation();
      event.preventDefault();
      if (deltaX > 0) {
        this.previousImage(item, event);
      } else {
        this.nextImage(item, event);
      }
    } else if (
      !item.touchMoved ||
      (absDeltaX <= this.TAP_MOVEMENT_THRESHOLD &&
        absDeltaY <= this.TAP_MOVEMENT_THRESHOLD &&
        deltaTime <= this.TAP_TIME_THRESHOLD)
    ) {
      this.viewListing(item.listing);
    }
    this.resetTouchState(item);
  }

  private resetTouchState(item: ExtendedWishlistItem): void {
    setTimeout(() => {
      item.touchStartX = undefined;
      item.touchStartY = undefined;
      item.touchStartTime = undefined;
      item.lastTouchX = undefined;
      item.lastTouchY = undefined;
      item.isTouchScrolling = false;
      item.touchMoved = false;
    }, 100);
  }

  onImageZoneClick(
    event: Event,
    item: ExtendedWishlistItem,
    zone: 'prev' | 'next' | 'view'
  ): void {
    event.stopPropagation();

    if (zone === 'view') {
      this.viewListing(item.listing);
    } else if (zone === 'prev') {
      this.previousImage(item, event);
    } else if (zone === 'next') {
      this.nextImage(item, event);
    }
  }

  previousImage(item: ExtendedWishlistItem, event: Event): void {
    event.stopPropagation();

    if (item.images.length <= 1) return;

    const newIndex =
      (item.currentImageIndex - 1 + item.images.length) % item.images.length;

    this.navigateToImage(item, newIndex);
  }

  nextImage(item: ExtendedWishlistItem, event: Event): void {
    event.stopPropagation();

    if (item.images.length <= 1) return;

    const newIndex = (item.currentImageIndex + 1) % item.images.length;

    this.navigateToImage(item, newIndex);
  }

  private navigateToImage(
    item: ExtendedWishlistItem,
    targetIndex: number
  ): void {
    item.isImageLoading = true;
    item.currentImageIndex = targetIndex;

    if (!item.preloadedImages) {
      item.preloadedImages = new Set();
    }
    item.preloadedImages.add(targetIndex);

    this.preloadAdjacentImages(item, targetIndex);
  }

  private preloadAdjacentImages(
    item: ExtendedWishlistItem,
    currentIndex: number
  ): void {
    if (item.images.length <= 1) return;

    const nextIndex = (currentIndex + 1) % item.images.length;
    const prevIndex =
      (currentIndex - 1 + item.images.length) % item.images.length;

    if (!item.preloadedImages?.has(nextIndex)) {
      this.preloadImage(item.images[nextIndex]);
      item.preloadedImages?.add(nextIndex);
    }

    if (!item.preloadedImages?.has(prevIndex)) {
      this.preloadImage(item.images[prevIndex]);
      item.preloadedImages?.add(prevIndex);
    }
  }

  private preloadImage(url: string): void {
    if (this.loadedImages.has(url)) return;

    const img = new Image();
    img.src = url;
    img.onload = () => {
      this.loadedImages.add(url);
    };
    img.onerror = () => {
      console.warn(`Failed to preload image: ${url}`);
    };
  }

  onItemHover(item: ExtendedWishlistItem): void {
    if (item.images.length <= 1) return;

    item.images.forEach((imageUrl, index) => {
      if (!item.preloadedImages?.has(index)) {
        this.preloadImage(imageUrl);
        item.preloadedImages?.add(index);
      }
    });

    if (!item.hasShownControls) {
      item.showCarouselControls = true;
      item.hasShownControls = true;

      if (item.controlsTimeout) {
        clearTimeout(item.controlsTimeout);
      }

      item.controlsTimeout = setTimeout(() => {
        item.showCarouselControls = false;
      }, 2000);
    }
  }

  onItemLeave(item: ExtendedWishlistItem): void {
    if (item.controlsTimeout) {
      clearTimeout(item.controlsTimeout);
    }
    item.hasShownControls = false;
    item.showCarouselControls = false;
  }

  hasMultipleImages(item: ExtendedWishlistItem): boolean {
    return item.images.length > 1;
  }

  getImageCounter(item: ExtendedWishlistItem): string {
    return `${item.currentImageIndex + 1}/${item.images.length}`;
  }

  onImageLoad(imageUrl: string, item?: ExtendedWishlistItem): void {
    this.loadedImages.add(imageUrl);
    if (item) {
      item.isImageLoading = false;
    }
  }

  isImageLoaded(imageUrl: string): boolean {
    return this.loadedImages.has(imageUrl);
  }

  goToPage(page: number): void {
    if (page === this.currentPage || this.isLoadingPage) return;

    this.currentPage = page;
    this.isLoadingPage = true;
    this.loadWishlist();
  }

  getVisiblePages(): (number | string)[] {
    if (!this.pagination) return [];

    const currentPage = this.pagination.page;
    const totalPages = this.pagination.pages;
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 4) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 3) {
        pages.push('...');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  }

  getDisplayRange(): string {
    if (!this.pagination) return '';

    const start = (this.pagination.page - 1) * this.pagination.limit + 1;
    const end = Math.min(
      this.pagination.page * this.pagination.limit,
      this.pagination.total
    );

    return `${start}-${end}`;
  }

  clearWishlist(): void {
    this.isClearing = true;

    this.wishlistService
      .clearWishlist()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.wishlistItems = [];
          this.filteredItems = [];
          this.summary = { total: 0, by_category: {} };
          this.pagination = null;
          this.isClearing = false;
          this.showClearConfirmation = false;
        },
        error: (error) => {
          this.error = error.message;
          this.isClearing = false;
          this.showClearConfirmation = false;
        },
      });
  }

  onWishlistChanged(event: any, item: ExtendedWishlistItem): void {
    if (event.action === 'removed') {
      this.wishlistItems = this.wishlistItems.filter(
        (i) => i.wishlist_id !== item.wishlist_id
      );
      this.applyFilter();
      this.loadWishlistSummary();
    }
  }

  viewListing(listing: any): void {
    const touchedItem = this.filteredItems.find(
      (item) => item.listing.id === listing.id
    );
    if (touchedItem) {
      if (touchedItem.isTouchScrolling || touchedItem.touchMoved) {
        return;
      }

      if (touchedItem.touchStartTime !== undefined) {
        const tapDuration = Date.now() - touchedItem.touchStartTime;
        if (tapDuration > this.TAP_TIME_THRESHOLD) {
          return;
        }
      }
    }
    const subcategory = this.getSubcategoryFromTableName(listing.table_name);
    this.router.navigate(['/listing-details', subcategory, listing.id]);
  }

  formatPrice(price: number, currency: string): string {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return formatter.format(price);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'today';
    } else if (diffDays === 1) {
      return 'yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  truncateDescription(description: string, maxLength: number = 120): string {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength).trim() + '...';
  }

  isNewListing(createdAt: string): boolean {
    const created = new Date(createdAt);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
    );
    return diffDays <= 3;
  }

  getCategoryDisplayName(tableName: string): string {
    const category = this.getCategoryFromTableName(tableName);
    const categoryNames: { [key: string]: string } = {
      vehicles: 'Vehicles',
      'real-estate': 'Real Estate',
      marketplace: 'Marketplace',
      jobs: 'Jobs',
    };
    return categoryNames[category] || 'Other';
  }

  getCategoryFromTableName(tableName: string): string {
    return this.wishlistService.getCategoryFromTableName(tableName);
  }

  getSubcategoryFromTableName(tableName: string): string {
    const subcategoryMap: { [key: string]: string } = {
      attributes_automobile: 'automobile',
      attributes_motorcycle: 'motorcycle',
      attributes_commercial_vehicles: 'commercial-vehicles',
      attributes_oldtimer: 'oldtimer',
      attributes_nautical: 'nautical',
      attributes_spare_parts: 'spare-parts',
      attributes_campers_caravans: 'campers-caravans',
      attributes_crashed_cars: 'crashed-cars',
      attributes_construction: 'constrution',
      attributes_agriculture: 'agriculture',
      attributes_house_sale: 'house-sale',
      attributes_house_rent: 'house-rent',
      attributes_apartments_sale: 'apartments-sale',
      attributes_apartments_rent: 'apartments-rent',
      attributes_land_sale: 'land-sale',
      attributes_land_rent: 'land-rent',
      attributes_business_office_sale: 'business-office-sale',
      attributes_business_office_rent: 'business-office-rent',
      attributes_garage_sale: 'garage-sale',
      attributes_garage_rent: 'garage-rent',
      attributes_luxury_real_estate_sale: 'luxury-real-estate-sale',
      attributes_luxury_real_estate_rent: 'luxury-real-estate-rent',
      attributes_room_rent: 'room-rent',
      attributes_roommate: 'looking-for-roommate',
      attributes_home: 'home',
      attributes_electronics: 'electronics',
      attributes_sport: 'sport',
      attributes_children: 'children',
      attributes_head_to_toe: 'head-to-toe',
      attributes_machines_tools: 'machines-tools',
      attributes_media: 'media',
      attributes_musical_instruments: 'musical-instruments',
      attributes_literature: 'literature',
      attributes_pets: 'pets',
      attributes_other: 'other',
      attributes_giving_away: 'giving-away',
      attributes_professional: 'professional',
      attributes_home_maintenance: 'home-maintenance',
      attributes_personal_care_wellness: 'personal-care-wellness',
      attributes_events_entertainment: 'events-entertainment',
      attributes_education_training: 'education-training',
      attributes_transport_delivery: 'transport-delivery',
    };

    return (
      subcategoryMap[tableName] ||
      tableName.replace('attributes_', '').replace(/_/g, '-')
    );
  }

  getDefaultImage(listing: any): string {
    const category = this.getCategoryFromTableName(listing.table_name);
    const defaultImages: { [key: string]: string } = {
      vehicles: '/assets/images/listing-placeholder/no-image-placeholder.png',
      'real-estate':
        '/assets/images/listing-placeholder/no-image-placeholder.png',
      marketplace:
        '/assets/images/listing-placeholder/no-image-placeholder.png',
      jobs: '/assets/images/listing-placeholder/no-image-placeholder.png',
    };
    return (
      defaultImages[category] ||
      '/assets/images/listing-placeholder/no-image-placeholder.png'
    );
  }

  onImageError(event: any, listing: any): void {
    event.target.src = this.getDefaultImage(listing);
  }

  trackByItem(index: number, item: ExtendedWishlistItem): string {
    return item.wishlist_id;
  }
}
